import { PageBreadcrumbItem } from "components/common/PageBreadcrumb";

// Interface para tipos de fornecedores
export interface FornecedorType {
  id: number;
  codigo: number;
  nome: string;
  nomeFantasia?: string;
  razaoSocial: string;
  cnpj: string;
  inscricaoEstadual?: string;
  telefone: string;
  email?: string;
  ativo: boolean;
  endereco?: EnderecoType;
}

export interface EnderecoType {
  cep: string;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  municipioId: number;
  municipio?: string; // Para exibição apenas
  uf?: string; // Para exibição apenas
  referencia?: string;
  telefone?: string;
}

// Enum para tipo de pessoa
export enum TipoPessoa {
  Fisica = 1,
  Juridica = 2
}

export interface CriarFornecedorDtoType {
  nome: string;
  nomeFantasia?: string;
  razaoSocial?: string;
  cnpj?: string;
  cpf?: string;
  inscricaoEstadual?: string;
  telefone: string;
  celular?: string;
  email?: string;
  dataNascimento?: string;
  endereco: EnderecoType;
  crt?: number;
  tipoPessoa: TipoPessoa;
  isCliente?: boolean;
  isFornecedorProduto?: boolean;
  prazoEntrega?: number;
  valorMinimo?: number;
}

export interface AlterarFornecedorDtoType {
  nome: string;
  nomeFantasia?: string;
  razaoSocial?: string;
  cnpj?: string;
  cpf?: string;
  inscricaoEstadual?: string;
  telefone: string;
  celular?: string;
  email?: string;
  dataNascimento?: Date | string;
  endereco: EnderecoType;
  crt?: number;
  tipoPessoa: TipoPessoa;
  isCliente?: boolean;
  isFornecedorProduto?: boolean;
  prazoEntrega?: number;
  valorMinimo?: number;
}

export interface FornecedorTableType {
  id: number;
  codigo: number;
  nome: string;
  nomeFantasia?: string;
  razaoSocial: string;
  cnpj: string;
  inscricaoEstadual?: string;
  telefone: string;
  email?: string;
  ativo: boolean;
  endereco?: {
    municipio: string;
    uf: string;
  };
}

export interface FornecedorApiResponse {
  data: FornecedorTableType[];
  total: number;
  pageIndex: number;
  pageSize: number;
  isSuccess: boolean;
  message: string;
}

// Filtros para fornecedores
export interface ActiveFilters {
  fornecedorStatus: "active" | "deleted" | "all";
}

// Breadcrumbs para fornecedores
export const fornecedoresBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: "Cadastros",
    url: "#!",
  },
  {
    label: "Fornecedores",
    active: true,
  },
];

export const fornecedorBreadcrumbItems = (
  codigo?: number
): PageBreadcrumbItem[] => [
  {
    label: "Cadastros", 
    url: "#!",
  },
  {
    label: "Fornecedores",
    url: "/cadastros/fornecedores",
  },
  {
    label: codigo?.toString() ?? "Novo",
    active: true,
  },
];

// Opções de UF
export const ufOptions = [
  { value: "AC", label: "AC - Acre" },
  { value: "AL", label: "AL - Alagoas" },
  { value: "AP", label: "AP - Amapá" },
  { value: "AM", label: "AM - Amazonas" },
  { value: "BA", label: "BA - Bahia" },
  { value: "CE", label: "CE - Ceará" },
  { value: "DF", label: "DF - Distrito Federal" },
  { value: "ES", label: "ES - Espírito Santo" },
  { value: "GO", label: "GO - Goiás" },
  { value: "MA", label: "MA - Maranhão" },
  { value: "MT", label: "MT - Mato Grosso" },
  { value: "MS", label: "MS - Mato Grosso do Sul" },
  { value: "MG", label: "MG - Minas Gerais" },
  { value: "PA", label: "PA - Pará" },
  { value: "PB", label: "PB - Paraíba" },
  { value: "PR", label: "PR - Paraná" },
  { value: "PE", label: "PE - Pernambuco" },
  { value: "PI", label: "PI - Piauí" },
  { value: "RJ", label: "RJ - Rio de Janeiro" },
  { value: "RN", label: "RN - Rio Grande do Norte" },
  { value: "RS", label: "RS - Rio Grande do Sul" },
  { value: "RO", label: "RO - Rondônia" },
  { value: "RR", label: "RR - Roraima" },
  { value: "SC", label: "SC - Santa Catarina" },
  { value: "SP", label: "SP - São Paulo" },
  { value: "SE", label: "SE - Sergipe" },
  { value: "TO", label: "TO - Tocantins" },
];

// Helper functions
export const formatarCnpj = (cnpj: string): string => {
  const cnpjLimpo = cnpj.replace(/\D/g, "");
  if (cnpjLimpo.length !== 14) return cnpj;
  
  return cnpjLimpo.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
};

export const formatarTelefone = (telefone: string): string => {
  const telefoneLimpo = telefone.replace(/\D/g, "");
  
  if (telefoneLimpo.length === 10) {
    return telefoneLimpo.replace(/^(\d{2})(\d{4})(\d{4})$/, "($1) $2-$3");
  } else if (telefoneLimpo.length === 11) {
    return telefoneLimpo.replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
  }
  
  return telefone;
};

export const formatarCep = (cep: string): string => {
  const cepLimpo = cep.replace(/\D/g, "");
  if (cepLimpo.length !== 8) return cep;
  
  return cepLimpo.replace(/^(\d{5})(\d{3})$/, "$1-$2");
};
